import { FC } from 'react';
import { Box, BoxProps } from '@mui/material';
import markdownit from 'markdown-it';
import DomPurify from 'dompurify';
import { clsx } from '@sweatpals/react-ui-kit';

const mdInstance = markdownit({
	html: true,
	linkify: true,
	xhtmlOut: true,
	breaks: false,
});

mdInstance.linkify.set({
	fuzzyIP: false,
	fuzzyEmail: true,
	fuzzyLink: true,
});

// Remember the old renderer if overridden, or proxy to the default renderer.
const defaultRender = mdInstance.renderer.rules['link_open'] || function (tokens, idx, options, env, self) {
  return self.renderToken(tokens, idx, options);
};

mdInstance.renderer.rules['link_open'] = function (tokens, idx, options, env, self) {
  // Add a new `target` attribute, or replace the value of the existing one.
  tokens[idx].attrSet('target', '_blank');
	tokens[idx].attrSet('rel', 'noopener noreferrer');

  // Pass the token to the default renderer.
  return defaultRender(tokens, idx, options, env, self);
};

export function createAndSanitizeMarkdown (input: string) {
	const dirtyHtml = mdInstance.render(input);
	const sanitized = DomPurify.sanitize(dirtyHtml, { ADD_ATTR: ['target', 'rel'] });

	return { __html: sanitized };
}

export interface MarkdownTextProps extends BoxProps {
	children: string;
	color?: string;
	linkColor?: string;
}

const MarkdownText: FC<MarkdownTextProps> = ({ children, color, linkColor, sx, ...props }) => {
	if (!children) {
		return null;
	}

	if (typeof children !== 'string') {
		return <>{children}</>;
	}

	return (
		<Box
			{...props}
			sx={clsx(
				{
					fontSize: '16px',
					lineHeight: '1.5em',

					display: 'flex',
					flexDirection: 'column',
					gap: '1em',
					color,

					'& ul, & ol': {
						display: 'flex',
						flexDirection: 'column',
						gap: '2px',
						pl: '2em',
						listStyle: 'outside',

						'& li': {
							lineHeight: '1.5em',
						},
					},

					'& b, & strong, & h1, & h2, & h3, & h4, & h5, & h6': {
						fontWeight: 700,
					},

					'& a': {
						color: `${linkColor || color || 'inherit'} !important`,
					},
				},
				sx
			)}
			component='div'
			dangerouslySetInnerHTML={createAndSanitizeMarkdown(children)}
		/>
	);
};

export default MarkdownText;
